import React, { useEffect, useRef, useState, useMemo } from 'react';
import { withStyles } from '@material-ui/styles';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
  useMediaQuery,
  useTheme,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Chip,
  Box,
} from '@material-ui/core';
import { useApolloClient } from '@apollo/client';
import SnackbarContext from '../../../containers/CustomSnackbar/SnackbarContext';
import UserPicker from '../../../components/common/AutoSuggest';
import getCompanyUsersQuery from '../../../graphql/companies/query/get-users';
import getAllUsersQuery from '../../../graphql/user/query/users';
import addUsersToTeamMutation from '../../../graphql/teams/mutation/add-users-to-team';

const R = require('ramda');

const styles = () => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
  },
  selectedUsersContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 16,
    padding: 8,
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    minHeight: 60,
  },
  pickerContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 8,
    alignItems: 'flex-start',
  },
});

function AddUserToTeamDialog(props) {
  const { classes, closeMenu, team, refetch } = props;
  const client = useApolloClient();
  const userPickerRef = useRef();
  
  const [companyUsers, setCompanyUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [includeExternalUsers, setIncludeExternalUsers] = useState(false);
  const [isLoadingAllUsers, setIsLoadingAllUsers] = useState(false);
  const [isLoadingCompanyUsers, setIsLoadingCompanyUsers] = useState(false);
  const [open, setOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  
  const eventCompanyId = team?.company?._id || localStorage.getItem('company_id');

  function handleClickOpen() {
    setOpen(true);
    closeMenu();
  }

  function handleClose() {
    setOpen(false);
    setSelectedUsers([]);
    setIncludeExternalUsers(false);
  }

  // Get current team members' IDs
  const currentTeamUserIds = useMemo(() => {
    return team?.users?.map((user) => user._id) || [];
  }, [team]);

  // Filter out users already in the team
  const availableUsers = useMemo(() => {
    const userPool = includeExternalUsers ? allUsers : companyUsers;
    return userPool.filter((user) => !currentTeamUserIds.includes(user._id));
  }, [companyUsers, allUsers, currentTeamUserIds, includeExternalUsers]);

  // Fetch company users when dialog opens
  useEffect(() => {
    if (!eventCompanyId || !open) {
      return;
    }
    console.log('Fetching company users', { eventCompanyId });
    setIsLoadingCompanyUsers(true);
    client
      .query({
        query: getCompanyUsersQuery,
        variables: { _id: eventCompanyId },
      })
      .then(({ error, data }) => {
        if (!data?.company?.users) {
          setIsLoadingCompanyUsers(false);
          return;
        }
        setCompanyUsers(data?.company?.users || []);
        setIsLoadingCompanyUsers(false);
      })
      .catch((err) => {
        console.error(err);
      });
  }, [eventCompanyId, open]);

  // Fetch all users when external users checkbox is enabled
  useEffect(() => {
    if (!includeExternalUsers || !open) {
      return;
    }
    console.log('Fetching all users for external selection');
    setIsLoadingAllUsers(true);
    client
      .query({
        query: getAllUsersQuery,
      })
      .then(({ error, data }) => {
        if (!data?.users) {
          setIsLoadingAllUsers(false);
          return;
        }
        console.log('Fetched all users', { users: data?.users });
        setAllUsers(data?.users || []);
        setIsLoadingAllUsers(false);
      })
      .catch((err) => {
        console.error(err);
        setIsLoadingAllUsers(false);
      });
  }, [includeExternalUsers, open]);

  const handleAddUser = () => {
    const picker = userPickerRef.current;
    const selectedUserName = picker?.state?.value;
    
    if (!selectedUserName) {
      return;
    }

    const selectedUserNameParts = selectedUserName.split(' ');
    const selectedUserFound = availableUsers.find((user) => {
      const userName = `${user.firstName} ${user.lastName}`;
      const userNameArray = userName.split(' ');
      return userNameArray.every((elem, ind) => elem === selectedUserNameParts[ind]);
    });

    if (selectedUserFound && !selectedUsers.find(u => u._id === selectedUserFound._id)) {
      setSelectedUsers([...selectedUsers, selectedUserFound]);
      picker.onChange(null, { newValue: '' });
      picker.onSuggestionsClearRequested();
    }
  };

  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter(user => user._id !== userId));
  };

  const handleSubmit = (snackbarContext) => {
    if (selectedUsers.length === 0) {
      snackbarContext.openSnackbar('error', 'Please select at least one user to add');
      return;
    }

    const userIds = selectedUsers.map(user => user._id);
    const variables = {
      tid: team._id,
      uids: userIds,
    };

    console.log('Adding users to team', variables);
    setIsSubmitting(true);

    client
      .mutate({
        mutation: addUsersToTeamMutation,
        variables,
      })
      .then((response) => {
        console.log('Added users to team ==>', response.data);
        const addedCount = variables.uids.length || 0;
        handleClose();
        snackbarContext.openSnackbar(
          'success',
          `Successfully added ${addedCount} user${addedCount !== 1 ? 's' : ''} to the team`
        );
        // If refetch is available, call it to update the team list after the mutation attempt.
        // This ensures the team list is updated regardless of success or failure.
        if (refetch) {
          refetch();
        }
      })
      .catch((error) => {
        snackbarContext.openSnackbar('error', error?.message || 'Failed to add users to team');
        console.error(error);
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <>
      <MenuItem onClick={handleClickOpen}>
        <Typography variant="body1" color="textSecondary">
          Add User
        </Typography>
      </MenuItem>

      <Dialog
        fullScreen={fullScreen}
        maxWidth="sm"
        fullWidth
        scroll="body"
        open={open}
        onClose={handleClose}
      >
        <DialogTitle id="form-dialog-title">Add Users to Team</DialogTitle>
        <DialogContent>
          <div className={classes.container}>
            <Typography variant="body2" color="textSecondary">
              Team: {team.name}
            </Typography>



            {isLoadingCompanyUsers && (
              <Box display="flex" alignItems="center" mb={2}>
                <CircularProgress size={20} style={{ marginRight: 10 }} />
                <Typography variant="body2">Loading users...</Typography>
              </Box>
            )}

            <div className={classes.pickerContainer}>
              <UserPicker
                users={availableUsers}
                ref={userPickerRef}
              />
              <Button
                onClick={handleAddUser}
                color="primary"
                variant="outlined"
                disabled={isLoadingAllUsers}
              >
                Add
              </Button>
            </div>

            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Selected Users ({selectedUsers.length})
              </Typography>
              <div className={classes.selectedUsersContainer}>
                {selectedUsers.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    No users selected
                  </Typography>
                ) : (
                  selectedUsers.map((user) => (
                    <Chip
                      key={user._id}
                      label={`${user.firstName} ${user.lastName}`}
                      onDelete={() => handleRemoveUser(user._id)}
                      color="primary"
                    />
                  ))
                )}
              </div>
            </Box>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="primary" disabled={isSubmitting}>
            Cancel
          </Button>
          <SnackbarContext.Consumer>
            {(snackbarContext) => (
              <Button
                onClick={() => handleSubmit(snackbarContext)}
                color="primary"
                disabled={isSubmitting || selectedUsers.length === 0}
              >
               {isSubmitting ? 'Adding...' : 'Add Users'}
              </Button>
            )}
          </SnackbarContext.Consumer>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default withStyles(styles)(AddUserToTeamDialog);
